import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Flag,
  Layers3,
  MapPin,
  Play,
  RotateCcw,
  Shield,
  Square,
  Users,
  Waypoints,
  XCircle,
} from 'lucide-react';
import { api } from '@/services/api';
import { getAdminSocket } from '@/services/socket';
import { Card, CardHeader } from '@/components/ui/card';
import { DataTable } from '@/components/tables/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Field } from '@/components/ui/field';
import { Drawer } from '@/components/ui/drawer';
import { EmptyState } from '@/components/ui/empty-state';
import { useToast } from '@/components/ui/toast';
import { PageHeader } from '@/components/ui/page-header';
import { useConfirmation } from '@/components/ui/confirmation';

const statusTone = {
  active: 'success',
  paused: 'warning',
  draft: 'neutral',
  cancelled: 'error',
  completed: 'accent',
};

const rideTypeLabels = {
  club_ride: 'Club ride',
  multi_club: 'Multi-club ride',
  open_ride: 'Open ride',
  invite_only: 'Invite only',
};

export function ConvoysPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState();
  const [tab, setTab] = useState('overview');
  const [label, setLabel] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [rideType, setRideType] = useState('club_ride');
  const [clubIds, setClubIds] = useState([]);
  const [roadCaptainId, setRoadCaptainId] = useState('');
  const [coCaptainId, setCoCaptainId] = useState('');
  const [subConvoys, setSubConvoys] = useState([]);
  const [busyId, setBusyId] = useState();
  const client = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirmation((state) => state.confirm);
  const query = useQuery({
    queryKey: ['admin-convoys'],
    queryFn: () => api('/api/v1/admin/trips').then((result) => result.data),
  });
  const clubs = useQuery({
    queryKey: ['admin-clubs'],
    queryFn: () => api('/api/v1/admin/groups').then((result) => result.data),
  });
  const members = useQuery({
    queryKey: ['admin-member-options'],
    queryFn: () => api('/api/v1/admin/members?limit=100').then((result) => result.data),
  });
  const refresh = () => client.invalidateQueries({ queryKey: ['admin-convoys'] });

  useEffect(() => {
    const socket = getAdminSocket();
    const refreshHandler = () => void refresh();
    socket.on('trip:status_updated', refreshHandler);
    socket.on('trip:destination_updated', refreshHandler);
    socket.on('ride:alert', refreshHandler);
    socket.on('connect', refreshHandler);
    return () => {
      socket.off('trip:status_updated', refreshHandler);
      socket.off('trip:destination_updated', refreshHandler);
      socket.off('ride:alert', refreshHandler);
      socket.off('connect', refreshHandler);
    };
  }, []);

  useEffect(() => {
    const id = searchParams.get('tripId');
    if (!id || !query.data?.length) return;
    const trip = query.data.find((item) => item._id === id);
    if (trip) open(trip);
  }, [query.data, searchParams]);

  const clubOptions = useMemo(() => (clubs.data ?? []).map((club) => ({
    value: club._id,
    label: club.name,
    description: `${club.memberCount ?? 0} riders · ${club.owner?.name ?? 'Owner unavailable'}`,
  })), [clubs.data]);
  const memberOptions = useMemo(() => (members.data ?? []).filter((member) => member.status === 'active').map((member) => ({
    value: member._id,
    label: member.name,
    description: `@${member.username}`,
  })), [members.data]);
  const rows = query.data ?? [];
  const summary = useMemo(() => ({
    active: rows.filter((item) => ['active', 'paused'].includes(item.status)).length,
    riders: rows.filter((item) => ['active', 'paused'].includes(item.status)).reduce((sum, item) => sum + Number(item.riderCount ?? 0), 0),
    safety: rows.reduce((sum, item) => sum + Number(item.openSafetyCount ?? 0), 0),
    multiClub: rows.filter((item) => item.rideType === 'multi_club').length,
  }), [rows]);

  const lifecycle = async (trip, action) => {
    setBusyId(trip._id);
    try {
      await api(`/api/v1/admin/trips/${trip._id}/${action}`, { method: 'POST' });
      await refresh();
      const messages = { start: 'Convoy started', restore: 'Convoy restored', cancel: 'Convoy cancelled', end: 'Convoy completed' };
      toast.show(messages[action] || 'Convoy updated', 'success');
    } catch (error) {
      toast.show(error.message, 'error', 'Action failed');
    } finally {
      setBusyId(undefined);
    }
  };

  const saveDestination = async () => {
    if (!selected) return;
    try {
      await api(`/api/v1/admin/trips/${selected._id}/destination`, {
        method: 'PATCH',
        body: JSON.stringify({
          latitude: Number(lat),
          longitude: Number(lon),
          label: label || undefined,
        }),
      });
      await refresh();
      toast.show('Destination updated', 'success');
    } catch (error) {
      toast.show(error.message, 'error', 'Destination update failed');
    }
  };

  const saveStructure = async () => {
    if (!selected) return;
    try {
      await api(`/api/v1/admin/trips/${selected._id}/collaboration`, {
        method: 'PATCH',
        body: JSON.stringify({
          rideType,
          participatingGroupIds: clubIds,
          roadCaptainId: roadCaptainId || null,
          coCaptainId: coCaptainId || null,
          subConvoys,
        }),
      });
      await refresh();
      toast.show('Convoy structure updated', 'success');
    } catch (error) {
      toast.show(error.message, 'error', 'Structure update failed');
    }
  };

  const buildSubConvoys = () => {
    const selectedClubs = (clubs.data ?? []).filter((club) => clubIds.includes(club._id));
    setSubConvoys(selectedClubs.map((club, index) => ({
      name: selectedClubs.length === 1 ? 'Main convoy' : `${String.fromCharCode(65 + index)} · ${club.name}`,
      groupIds: [club._id],
      memberIds: [],
      order: index,
    })));
  };

  function open(trip) {
    setSelected(trip);
    setTab('overview');
    setLabel(trip.destination?.label ?? '');
    setLon(String(trip.destination?.coordinates?.[0] ?? ''));
    setLat(String(trip.destination?.coordinates?.[1] ?? ''));
    setRideType(trip.rideType ?? 'club_ride');
    setClubIds((trip.participatingClubs ?? []).map((club) => club._id).length
      ? trip.participatingClubs.map((club) => club._id)
      : [String(trip.organizerGroupId ?? trip.groupId)]);
    setRoadCaptainId(trip.roadCaptain?._id ?? trip.roadCaptainId ?? trip.createdBy ?? '');
    setCoCaptainId(trip.coCaptain?._id ?? trip.coCaptainId ?? '');
    setSubConvoys((trip.subConvoys ?? []).map((item) => ({
      name: item.name,
      leaderId: item.leaderId ?? null,
      sweeperId: item.sweeperId ?? null,
      memberIds: item.memberIds ?? [],
      groupIds: item.groupIds ?? [],
      order: item.order ?? 0,
    })));
    setSearchParams({ tripId: trip._id });
  }

  const close = () => {
    setSelected(undefined);
    setSearchParams({});
  };

  const columns = [
    {
      key: 'convoy',
      header: 'Convoy',
      sortValue: (row) => row.name,
      cell: (row) => <div className="max-w-sm"><p className="font-semibold text-slate-100">{row.name}</p><p className="mt-1 flex items-center gap-1 text-[10px] text-slate-600"><Users size={10}/>{row.organizerClub?.name ?? 'Club unavailable'} · {rideTypeLabels[row.rideType] ?? row.rideType}</p></div>,
    },
    {
      key: 'clubs',
      header: 'Clubs',
      sortValue: (row) => row.participatingClubs?.length ?? 0,
      cell: (row) => <div><p className="font-mono text-sm font-black text-white">{row.participatingClubs?.length ?? 1}</p><p className="max-w-52 truncate text-[10px] text-slate-600">{row.participatingClubs?.map((club) => club.name).join(', ') || row.organizerClub?.name}</p></div>,
    },
    {
      key: 'riders',
      header: 'Riders',
      sortValue: (row) => row.riderCount ?? 0,
      cell: (row) => <div><p className="font-mono text-sm font-black text-slate-100">{row.onlineCount ?? 0}/{row.riderCount ?? 0}</p><p className="text-[9px] uppercase tracking-[.12em] text-slate-700">online</p></div>,
    },
    {
      key: 'captain',
      header: 'Road captain',
      sortValue: (row) => row.roadCaptain?.name ?? '',
      cell: (row) => <div className="flex items-center gap-2"><Flag size={13} className="text-cyan-300"/><span className="text-xs text-slate-300">{row.roadCaptain?.name ?? 'Not assigned'}</span></div>,
    },
    {
      key: 'destination',
      header: 'Destination',
      sortValue: (row) => row.destination?.label ?? '',
      cell: (row) => <div className="max-w-56"><p className="truncate text-xs text-slate-300">{row.destination?.label ?? 'Coordinates only'}</p><p className="mt-1 font-mono text-[9px] text-slate-700">{row.destination?.coordinates?.join(', ') ?? '—'}</p></div>,
    },
    {
      key: 'safety',
      header: 'Safety',
      sortValue: (row) => row.openSafetyCount ?? 0,
      cell: (row) => <Badge tone={(row.openSafetyCount ?? 0) > 0 ? 'error' : 'success'}>{row.openSafetyCount ?? 0} open</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (row) => row.status,
      cell: (row) => <Badge tone={statusTone[row.status] ?? 'neutral'}>{row.status}</Badge>,
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      exportable: false,
      hideable: false,
      cell: (row) => <Button variant="secondary" className="h-8 px-3 text-xs" onClick={(event) => { event.stopPropagation(); open(row); }}><ExternalLink size={12}/>Open</Button>,
    },
  ];

  return <>
    <div className="workspace-page">
      <PageHeader eyebrow="Operations" title="Convoy Sessions" meta={<Badge tone="accent">{rows.length} sessions</Badge>}/>
      <div className="grid gap-3 sm:grid-cols-4"><SummaryCard label="Active" value={summary.active} icon={Waypoints}/><SummaryCard label="Riders on road" value={summary.riders} icon={Users}/><SummaryCard label="Open safety" value={summary.safety} icon={AlertTriangle} warning={summary.safety > 0}/><SummaryCard label="Multi-club" value={summary.multiClub} icon={Layers3}/></div>
      <Card className="workspace-fill"><CardHeader eyebrow="Ride operations" title="Convoys"/>{rows.length ? <DataTable
        tableId="convoys"
        rows={rows}
        columns={columns}
        getKey={(row) => row._id}
        onRowClick={open}
        onRefresh={() => query.refetch()}
        loading={query.isFetching}
        searchPlaceholder="Search convoy, club, captain, or destination"
        bulkActions={[{
          label: 'Cancel',
          variant: 'danger',
          icon: <XCircle size={12}/>,
          onClick: async (selectedRows, clear) => {
            const active = selectedRows.filter((row) => ['active', 'paused'].includes(row.status));
            if (!active.length || !await confirmAction({ title: `Cancel ${active.length} convoy(s)?`, message: 'Live tracking and active ride state will be stopped for the selected convoys.', confirmLabel: 'Cancel convoys', tone: 'danger' })) return;
            await Promise.all(active.map((row) => api(`/api/v1/admin/trips/${row._id}/cancel`, { method: 'POST' })));
            clear();
            await refresh();
          },
        }]}
      /> : <EmptyState title={query.isLoading ? 'Loading convoys' : 'No convoy sessions'}/>}</Card>
    </div>

    <Drawer open={Boolean(selected)} title={selected?.name ?? 'Convoy'} eyebrow="Convoy control" onClose={close} width="max-w-5xl">
      {selected && <div className="space-y-5">
        <div className="flex flex-wrap gap-2 border-b border-white/8 pb-3">{['overview', 'structure', 'destination'].map((name) => <button key={name} type="button" onClick={() => setTab(name)} className={`h-8 border px-3 text-[10px] font-black uppercase tracking-[.12em] ${tab === name ? 'border-cyan-300/30 bg-cyan-300/[.07] text-cyan-100' : 'border-white/8 text-slate-500'}`}>{name}</button>)}</div>
        {tab === 'overview' && <div className="space-y-5">
          <div className="grid gap-2 sm:grid-cols-4"><Info label="Status" value={selected.status}/><Info label="Riders" value={`${selected.onlineCount ?? 0}/${selected.riderCount ?? 0}`}/><Info label="Clubs" value={selected.participatingClubs?.length ?? 1}/><Info label="Sub-convoys" value={selected.subConvoyCount ?? 0}/></div>
          <div className="grid gap-3 md:grid-cols-2"><Panel title="Command"><Line label="Organizer" value={selected.organizerClub?.name ?? '—'}/><Line label="Road captain" value={selected.roadCaptain?.name ?? 'Not assigned'}/><Line label="Co-captain" value={selected.coCaptain?.name ?? 'Not assigned'}/></Panel><Panel title="Safety"><Line label="Open alerts" value={selected.openSafetyCount ?? 0}/><Line label="Emergency riders" value={selected.emergencyCount ?? 0}/><Line label="Destination" value={selected.destination?.label ?? 'Coordinates only'}/></Panel></div>
          <div className="flex flex-wrap gap-2">{selected.status === 'draft' && <Button disabled={busyId === selected._id} onClick={() => void lifecycle(selected, 'start')}><Play size={14}/>Start convoy</Button>}{['active', 'paused'].includes(selected.status) && <><Button variant="secondary" disabled={busyId === selected._id} onClick={() => void lifecycle(selected, 'end')}><CheckCircle2 size={14}/>Complete</Button><Button variant="danger" disabled={busyId === selected._id} onClick={() => void lifecycle(selected, 'cancel')}><Square size={14}/>Cancel</Button></>}{selected.status === 'cancelled' && <Button disabled={busyId === selected._id} onClick={() => void lifecycle(selected, 'restore')}><RotateCcw size={14}/>Restore</Button>}</div>
        </div>}
        {tab === 'structure' && <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2"><Field label="Ride type"><Select value={rideType} onChange={(event) => setRideType(event.target.value)}><option value="club_ride">Club ride</option><option value="multi_club">Multi-club ride</option><option value="open_ride">Open ride</option><option value="invite_only">Invite only</option></Select></Field><Field label="Participating clubs" hint={`${clubIds.length} selected`}><MultiSelect value={clubIds} onChange={setClubIds} options={clubOptions} placeholder="Select clubs"/></Field><Field label="Road captain"><Select value={roadCaptainId} onChange={(event) => setRoadCaptainId(event.target.value)}><option value="">Not assigned</option>{memberOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</Select></Field><Field label="Co-captain"><Select value={coCaptainId} onChange={(event) => setCoCaptainId(event.target.value)}><option value="">Not assigned</option>{memberOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</Select></Field></div>
          <div className="border border-white/8 bg-black/10"><div className="flex items-center justify-between border-b border-white/8 px-4 py-3"><div><p className="text-xs font-bold text-slate-200">Sub-convoys</p><p className="text-[10px] text-slate-600">Each group can have its own leader and sweeper.</p></div><Button variant="secondary" className="h-8 px-3 text-xs" onClick={buildSubConvoys}><Layers3 size={13}/>Build by club</Button></div><div className="space-y-2 p-3">{subConvoys.length === 0 ? <p className="py-6 text-center text-xs text-slate-600">No sub-convoy configured.</p> : subConvoys.map((item, index) => <div key={`${item.name}-${index}`} className="grid gap-2 border border-white/8 bg-white/[.02] p-3 md:grid-cols-[1fr_1fr_1fr_auto]"><Input value={item.name} onChange={(event) => setSubConvoys((current) => current.map((entry, currentIndex) => currentIndex === index ? { ...entry, name: event.target.value } : entry))} placeholder="Sub-convoy name"/><Select value={item.leaderId ?? ''} onChange={(event) => setSubConvoys((current) => current.map((entry, currentIndex) => currentIndex === index ? { ...entry, leaderId: event.target.value || null } : entry))}><option value="">Leader not assigned</option>{memberOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</Select><Select value={item.sweeperId ?? ''} onChange={(event) => setSubConvoys((current) => current.map((entry, currentIndex) => currentIndex === index ? { ...entry, sweeperId: event.target.value || null } : entry))}><option value="">Sweeper not assigned</option>{memberOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</Select><Button variant="danger" className="h-10 px-3" onClick={() => setSubConvoys((current) => current.filter((_, currentIndex) => currentIndex !== index))}><XCircle size={14}/></Button></div>)}</div></div>
          <Button onClick={() => void saveStructure()}><Shield size={14}/>Save structure</Button>
        </div>}
        {tab === 'destination' && <div className="max-w-xl space-y-4"><Field label="Destination"><Input placeholder="Destination label" value={label} onChange={(event) => setLabel(event.target.value)}/></Field><div className="grid grid-cols-2 gap-3"><Field label="Latitude"><Input placeholder="Latitude" value={lat} onChange={(event) => setLat(event.target.value)}/></Field><Field label="Longitude"><Input placeholder="Longitude" value={lon} onChange={(event) => setLon(event.target.value)}/></Field></div><Button disabled={!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lon))} onClick={() => void saveDestination()}><MapPin size={14}/>Update destination</Button></div>}
      </div>}
    </Drawer>
  </>;
}

export const TripsPage = ConvoysPage;

function SummaryCard({ label, value, icon: Icon, warning = false }) {
  return <div className="flex items-center gap-3 border border-white/8 bg-[#0a1017]/90 p-4"><span className={`grid size-10 place-items-center border ${warning ? 'border-rose-400/25 bg-rose-400/[.06] text-rose-300' : 'border-cyan-300/20 bg-cyan-300/[.045] text-cyan-300'}`}><Icon size={16}/></span><div><p className="font-mono text-xl font-black text-white">{value}</p><p className="text-[9px] font-black uppercase tracking-[.15em] text-slate-600">{label}</p></div></div>;
}

function Info({ label, value }) {
  return <div className="border border-white/8 bg-white/[.02] p-3"><p className="text-[9px] font-black uppercase tracking-[.14em] text-slate-700">{label}</p><p className="mt-1 truncate text-sm font-bold capitalize text-slate-200">{value}</p></div>;
}

function Panel({ title, children }) {
  return <div className="border border-white/8 bg-black/10"><p className="border-b border-white/8 px-4 py-3 text-xs font-bold text-slate-200">{title}</p><div className="space-y-3 p-4">{children}</div></div>;
}

function Line({ label, value }) {
  return <div className="flex items-center justify-between gap-4 text-xs"><span className="text-slate-600">{label}</span><span className="truncate font-semibold text-slate-300">{value}</span></div>;
}
